import _ from 'lodash'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useCallback } from 'react'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { listWaitingRooms, selectWaitingRooms, selectWaitingRoomSearchParams, selectWaitingRoomSearchResult } from '@/redux/reducers/waiting-room.reducer'

import SelectWaitingRoomStep from '../components/SelectWaitingRoomStep'
import { CreateConsultationStepperContext } from '../CreateConsultationStepper'
import { useWaitingRoomPreferences } from '../hooks/useWaitingRoomPreferences'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'
import { WaitingRoom } from '@/core/models/waiting-room.model'
import { toDoctorIds } from '@/hooks/useRoomDoctorStatuses'

function enrichWaitingRoom(waitingRoom: WaitingRoom) {
  const openingHour = _.find(waitingRoom.openingHours, ({ day }) => day === dayjs().day())
  let isOpening = false
  if (openingHour) {
    const { periods } = openingHour
    isOpening = _.some(periods, (period) => dayjs().isBetween(dayjs(period.open, 'HH:mm'), dayjs(period.close, 'HH:mm')))
  }
  return {
    id: waitingRoom._id,
    doctorIds: toDoctorIds(waitingRoom.doctors),
    isOpening,
    name: waitingRoom.name,
    avgWaitingTime: waitingRoom.avgWaitingTime,
  }
}

export default function SelectWaitingRoomStepContainer() {
  const { setValue, values } = useContext(CreateConsultationStepperContext)
  const dispatch = useAppDispatch()

  const waitingRooms = useAppSelector(selectWaitingRooms)
  const waitingRoomSearchParams = useAppSelector(selectWaitingRoomSearchParams)
  const waitingRoomSearchResult = useAppSelector(selectWaitingRoomSearchResult)

  const { favoriteIds, lastSelectedId, toggleFavorite, isFavorite, saveLastSelected } = useWaitingRoomPreferences()

  // Fetch favorite waiting rooms by IDs (separate query, always loaded)
  const { data: favoriteRoomsData } = useListWaitingRoomsQuery(
    { query: { _id: { $in: favoriteIds } }, options: { limit: favoriteIds.length || 1, skip: 0 } },
    { skip: favoriteIds.length === 0 }
  )

  const favoriteWaitingRooms = useMemo(
    () => (favoriteRoomsData?.items ?? []).map(enrichWaitingRoom),
    [favoriteRoomsData]
  )

  // Fetch last selected waiting room if not already in favorites or paginated list
  const { data: lastSelectedData } = useListWaitingRoomsQuery(
    { query: { _id: lastSelectedId! }, options: { limit: 1, skip: 0 } },
    { skip: !lastSelectedId }
  )

  const lastSelectedRoom = useMemo(() => {
    const room = lastSelectedData?.items?.[0]
    return room ? enrichWaitingRoom(room) : null
  }, [lastSelectedData])

  const waitingRoomsData = useMemo(
    () => _.map(waitingRooms, enrichWaitingRoom),
    [waitingRooms]
  )

  const waitingRoomTotalCount = useMemo(() => _.get(waitingRoomSearchResult, 'totalCount', 0), [waitingRoomSearchResult])

  useEffect(() => {
    if (values.waitingRoom) setValue('waitingRoom', undefined)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    dispatch(listWaitingRooms({}, { limit: 5, skip: 0 }))
  }, [dispatch])

  const handlePageChange = (page: number, rowsPerPage: number) => {
    dispatch(listWaitingRooms({}, { limit: rowsPerPage, skip: (page - 1) * rowsPerPage }))
  }

  const handleSelect = useCallback(({ id }: { id: string }) => {
    setValue('waitingRoom', id)
    saveLastSelected(id)
  }, [setValue, saveLastSelected])

  const handleTextSearch = (text: string) => {
    dispatch(listWaitingRooms({ name: text }, { limit: _.get(waitingRoomSearchParams, 'options.limit', 5), skip: 0 }))
  }

  const handleToggleFavorite = useCallback((id: string) => {
    toggleFavorite(id)
  }, [toggleFavorite])

  return (
    <SelectWaitingRoomStep
      data={waitingRoomsData}
      favoriteData={favoriteWaitingRooms}
      lastSelectedRoom={lastSelectedRoom}
      isFavorite={isFavorite}
      onPageChange={handlePageChange}
      onSelect={handleSelect}
      onTextSearch={handleTextSearch}
      onToggleFavorite={handleToggleFavorite}
      totalCount={waitingRoomTotalCount}
    />
  )
}
